/* istanbul ignore file */
import React, { useEffect, useState } from 'react';
import { IntlShape, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { get, isEmpty, isNil } from 'lodash';
import ClaimInformation from 'containers/PreOpeningPage/ClaimInformation';
import ClaimCircumstances from 'containers/PreOpeningPage/ClaimCircumstances';
import ClaimDeclaration from 'containers/PreOpeningPage/ClaimDeclaration';
import VictimCard from 'containers/PreOpeningPage/VictimCard';
import UploadDocumentsBloc from 'containers/RecapPage/uploadDocumentsBloc';
import { Listings } from 'containers/AppContainer/typings';
import {
  CLAIM_STATUS,
  EMPTY_CLAIM_TO_PREOPEN,
} from 'containers/data-structure';
import OpposingPartyRecapContainer from 'containers/PreOpeningPage/OpposingParty/OpposingPartyRecapContainer';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import FraudeInformation from 'containers/PreOpeningPage/fraude';
import { renderSearchQuery } from 'containers/ExpertAssignedMissionShow/utils';
import { IUploadedDocument } from 'containers/LawyerLegalProceedingsPage/typings';
import {
  EMPTY_SUPPORT_DOC,
  PV_DOC_ID,
} from 'containers/LawyerRecapPage/constants';
import { CorporealDocument } from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/typings';
import LawyerClaimCircumstances from 'containers/LawyerRecapPage/LawyerClaimCircumstances';
import CoreButton from 'components/Buttons/CoreButton';
import { resolveSagisClaim } from 'helpers/utils';
import { rcCoporeal } from 'containers/CourtManagerLegalProceedings/constants';
import { extractOpposingParties } from 'containers/PreOpeningPage/utils';
import { KeycloakUser } from 'containers/RecapManagerPage/typings';
import { ClaimRecapType } from 'typings/claimTypes';

export interface ClaimRecapProps {
  intl: IntlShape;
  listings: Listings;
  storedClaimToPreopen: any;
  claimId: string;
  requestClaimRecap: Function;
  match: { params: { claimId: number } };
  claimRecap: ClaimRecapType;
  policyInfo: { sagis: {} };
  requestSearchClaims: Function;
  resetSearchClaimsStore: Function;
  setClaimRecap: Function;
  requestRejectClaim: Function;
  requestOpenClaim: Function;
  requestAssigment: Function;
  requestCorporealLawyers: Function;
  requestOpenMixedClaim: Function;
  corporealLawyers: KeycloakUser[];
  openedClaim: any;
  requestUpdateVictimInfo?: Function;
  requestCreateVehicle?: Function;
  requestValidateVictim?: Function;
  requestValidateVehicle: Function;
  requestAddVictims?: Function;
  requestUpdateVehicleInfo: Function;
  requestUpdateClaim?: Function;
  resetUploadDocumentStore?: Function;
  uploadDocument?: Function;
  uploadedDocument: IUploadedDocument;
  setDocumentId?: Function;
  documentId: string;
  requestAddDocumentClaim?: Function;
  requestValidateProvisionIntervenant?: Function;
  requestUnlockClaim: Function;
  requestLockClaim: Function;
  setLockedClaim: Function;
  resetStoreClaim: Function;
  requestGetReportsPolicy: Function;
  requestSagisClaimInfo: Function;
  requestGetDocByDocumentumId: Function;
}

const Container = styled.div`
  &&.titleContainer {
    display: flex;
    margin-top: 50px;
    margin-bottom: 10px;
  }
  .title {
    width: 180px;
    text-align: left;
    font-family: Roboto;
    letter-spacing: 0;
    color: #2b2727;
    opacity: 1;
    font-size: 24px;
    margin-top: 63px;
  }
  .validationButton {
    float: right;
  }
`;

const GlobalBloc = styled.div`
  &&.globalBloc {
    position: relative;
    margin-top: 150px;
  }
`;
export const StyledButton = styled(CoreButton)`
  margin-top: 10px;
  margin-bottom: 20px;
  width: 300px;
  height: 40px;
  background: #ffc912;
  &:hover {
    background-color: #ffc912;
  }
`;
export const ClaimRecap: React.FC<ClaimRecapProps> = (props): JSX.Element => {
  useEffect(() => {
    return () => {
      props.resetStoreClaim();
      props.resetSearchClaimsStore();
    };
  }, []);
  const [dataClaimToUpdate, setDataClaimToUpdate] = useState<any>({});

  useEffect(() => {
    props.requestClaimRecap(props.match.params.claimId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.match.params.claimId]);

  useEffect(() => {
    props.resetSearchClaimsStore();
    if (!isEmpty(props.claimRecap)) {
      const searchQuery = renderSearchQuery(props.claimRecap);
      props.requestSearchClaims(searchQuery);
      if (!isNil(get(props.claimRecap, 'claim.claimNumber', null))) {
        props.requestSagisClaimInfo(
          get(props.claimRecap, 'claim.claimNumber', null),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.claimRecap]);

  const getClaimNumber = () => {
    const claim = get(props.claimRecap, 'claim', {});
    return get(claim, 'claimNumber', '');
  };

  const [updatedPVDocument, setUpdatedPVDocument] = useState({
    ...EMPTY_SUPPORT_DOC,
  });

  const [isFistUpdate, setIsFistUpdate] = useState<boolean>(true);

  useEffect(() => {
    if (!isEmpty(props.claimRecap) && isFistUpdate) {
      setDataClaimToUpdate(props.claimRecap);
      setIsFistUpdate(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.claimRecap]);

  const deletePVDocument = (document: CorporealDocument) => {
    setUpdatedPVDocument({ ...EMPTY_SUPPORT_DOC });
  };

  const setDocument = (document: CorporealDocument) => {
    if (props.requestAddDocumentClaim)
      props.requestAddDocumentClaim({
        ...document,
        claimId: props.match.params.claimId,
        claimNumber: getClaimNumber(),
        isInboxesActive: featureService.hasFeature(
          FEATURES.ATTRIBUTION_INBOXES_MANAGER,
        ),
      });
    if (props.resetUploadDocumentStore) props.resetUploadDocumentStore();
    props.requestClaimRecap(props.claimId);
  };

  const setPVDocument = (document: CorporealDocument) => {
    if (props.documentId === PV_DOC_ID) {
      setUpdatedPVDocument(document);
      if (props.resetUploadDocumentStore) props.resetUploadDocumentStore();
    }
  };

  return (
    <GlobalBloc className="recap-bloc">
      {!isEmpty(props.claimRecap) && !isEmpty(props.policyInfo) && (
        <Container>
          <ClaimDeclaration
            isRecapPage
            isManager={false}
            isReadOnly={true}
            inputProps={{ disableUnderline: true }}
            disableInput
            inputLabelProps
            listings={props.listings}
            claimToPreopen={EMPTY_CLAIM_TO_PREOPEN}
            storedClaimToPreopen={props.claimRecap}
            policyInfo={props.policyInfo}
            setClaimRecap={props.setClaimRecap}
            isOpeningManager={false}
            setDataClaimToUpdate={setDataClaimToUpdate}
            dataClaimToUpdate={dataClaimToUpdate}
          />
          <ClaimInformation
            isRecapPage
            isManager={false}
            isReadOnly={true}
            inputProps={{ disableUnderline: true }}
            disableInput
            inputLabelProps
            listings={props.listings}
            claimToPreopen={EMPTY_CLAIM_TO_PREOPEN}
            storedClaimToPreopen={props.claimRecap}
            policyInfo={props.policyInfo}
            setClaimRecap={props.setClaimRecap}
            stakeholders={[]}
            isOpeningManager={false}
            setDataClaimToUpdate={setDataClaimToUpdate}
            dataClaimToUpdate={dataClaimToUpdate}
          />
          {featureService.hasFeature(FEATURES.OPPOSING_PARTY) && (
            <OpposingPartyRecapContainer
              isManager={true}
              isConsultation={true}
              isLockedPage={true}
              claimToPreopen={EMPTY_CLAIM_TO_PREOPEN}
              storedClaimToPreopen={props.claimRecap}
              listings={props.listings}
              policyInfo={props.policyInfo}
              setClaimRecap={props.setClaimRecap}
              requestCreateVehicle={props.requestCreateVehicle}
              requestValidateVehicle={props.requestValidateVehicle}
              requestUpdateVehicleInfo={props.requestUpdateVehicleInfo}
              setDataClaimToUpdate={setDataClaimToUpdate}
              dataClaimToUpdate={dataClaimToUpdate}
              sagisOpposingParties={extractOpposingParties(
                get(resolveSagisClaim(props.policyInfo), 'offeredRisks', []),
              )}
            />
          )}
          {get(props.claimRecap, 'claim.status') !== CLAIM_STATUS.VALIDATED ? (
            <ClaimCircumstances
              isRecapPage
              isManager
              isReadOnly={false}
              inputProps={{ disableUnderline: true }}
              disableInput
              inputLabelProps
              listings={props.listings}
              claimToPreopen={EMPTY_CLAIM_TO_PREOPEN}
              storedClaimToPreopen={props.claimRecap}
              setClaimRecap={props.setClaimRecap}
              claimCircumstancesSagis={get(
                resolveSagisClaim(props.policyInfo),
                'offeredRisks',
                [],
              )?.find(
                (element: { riskName: string }) =>
                  element.riskName === rcCoporeal,
              )}
              isConsultation={true}
            />
          ) : (
            <LawyerClaimCircumstances
              isRecapPage
              isReadOnly
              inputProps={{ disableUnderline: true }}
              inputLabelProps
              listings={props.listings}
              claimToPreopen={EMPTY_CLAIM_TO_PREOPEN}
              storedClaimToPreopen={props.claimRecap}
              setClaimRecap={props.setClaimRecap}
              setDataClaimToUpdate={setDataClaimToUpdate}
              dataClaimToUpdate={dataClaimToUpdate}
              isOpeningManager={false}
            />
          )}
          <VictimCard
            isRecapPage
            isManager={false}
            isReadOnly={true}
            isLockedPage={true}
            disableInput
            listings={props.listings}
            claimToPreopen={EMPTY_CLAIM_TO_PREOPEN}
            storedClaimToPreopen={props.claimRecap}
            setClaimRecap={props.setClaimRecap}
            isOpeningManager={false}
            policyInfo={props.policyInfo}
            requestUpdateVictimInfo={props.requestUpdateVictimInfo}
            requestValidateVictim={props.requestValidateVictim}
            requestCreateVictim={props.requestAddVictims}
            updateFilde={() => null}
            setDataClaimToUpdate={setDataClaimToUpdate}
            dataClaimToUpdate={dataClaimToUpdate}
          />
          <UploadDocumentsBloc
            storedClaimToPreopen={props.claimRecap}
            isManager={false}
            isRecap
            isReadOnly={true}
            index={0}
            resetUploadedDocument={props.resetUploadDocumentStore}
            uploadDocument={props.uploadDocument}
            uploadedDocument={props.uploadedDocument}
            setDocumentId={props.setDocumentId}
            docId={props.documentId}
            pvDocument={updatedPVDocument}
            setPVDocument={setPVDocument}
            deletePVDocument={deletePVDocument}
            setDataClaimToUpdate={setDataClaimToUpdate}
            dataClaimToUpdate={dataClaimToUpdate}
            setDocument={setDocument}
            requestGetReportsPolicy={props.requestGetReportsPolicy}
            requestGetDocByDocumentumId={props.requestGetDocByDocumentumId}
          />
          {featureService.hasFeature(FEATURES.FRAUDE_CORPO) && (
            <FraudeInformation
              isRecapPage
              disableInput
              updateFilde={() => null}
              storedClaimToPreopen={props.claimRecap}
              claimToPreopen={EMPTY_CLAIM_TO_PREOPEN}
              setDataClaimToUpdate={setDataClaimToUpdate}
              dataClaimToUpdate={dataClaimToUpdate}
              listings={props.listings}
            />
          )}
        </Container>
      )}
    </GlobalBloc>
  );
};

export default injectIntl(ClaimRecap);
